import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, forwardRef, useCallback } from "react";
import { cn } from "../utils";
import { MFACodeDigit } from "./MFACodeDigit";
import { useMfaCodeInputImperativeFocus, useMfaCodeInputSlots } from "./mfa-code-input.hooks";
/**
 * MFA / OTP digit row: presentation + behavior only (no i18n, no API).
 * Digit chrome uses `@fmc-digital/ui/styles.css` (`.mfa-code-input*`); layout uses Tailwind spacing scale.
 */
function MFACodeInputInner({ code, onChange, length = 6, error = false, disabled = false, success = false, testID = "mfa-code-input", className, digitClassName, autoComplete = "off", }, ref) {
    const inputRefs = useRef([]);
    const { slotKeys, paddedCode } = useMfaCodeInputSlots(code, length);
    useMfaCodeInputImperativeFocus(ref, inputRefs);
    const handleCodeChange = useCallback((index, value) => {
        const newCode = [...paddedCode];
        newCode[index] = value;
        onChange(newCode);
        if (value && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    }, [paddedCode, onChange, length]);
    const handleKeyDown = useCallback((index, e) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            if (paddedCode[index]) {
                const newCode = [...paddedCode];
                newCode[index] = "";
                onChange(newCode);
                if (index > 0) {
                    inputRefs.current[index - 1]?.focus();
                }
                else {
                    setTimeout(() => {
                        inputRefs.current[0]?.focus();
                    }, 0);
                }
            }
            else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        }
    }, [paddedCode, onChange]);
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const pastedData = e.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, length);
        const newCode = [...paddedCode];
        pastedData.split("").forEach((digit, i) => {
            if (i < length) {
                newCode[i] = digit;
            }
        });
        onChange(newCode);
        const cellCount = newCode.length;
        const maxFocusIndex = cellCount > 0 ? cellCount - 1 : 0;
        const nextIndex = Math.min(pastedData.length, maxFocusIndex);
        inputRefs.current[nextIndex]?.focus();
    }, [paddedCode, onChange, length]);
    return (_jsx("div", { "data-testid": testID, className: cn("flex gap-3 justify-center w-fit max-w-full mb-4 md:mb-0", className), children: slotKeys.map((slotKey, index) => (_jsx(MFACodeDigit, { ref: (el) => {
                inputRefs.current[index] = el;
            }, className: digitClassName, value: paddedCode[index] ?? "", onChange: (value) => handleCodeChange(index, value), onKeyDown: (e) => handleKeyDown(index, e), onPaste: index === 0 ? handlePaste : undefined, index: index, error: error, disabled: disabled, success: success, autoComplete: autoComplete }, slotKey))) }));
}
export const MFACodeInput = forwardRef(MFACodeInputInner);
MFACodeInput.displayName = "MFACodeInput";
//# sourceMappingURL=MFACodeInput.js.map